// Where the reports answer.
//
// Its own module for the same reason auth/endpoints.ts is, and for a second one
// that file did not have to care about.
//
// The first reason is the cycle. rateLimit.ts names urls in order to limit them,
// and routes.ts reaches into rateLimit.ts for clientIp; two modules importing
// each other resolve in whichever order the loader happens to pick, and the way
// that fails is a constant read before it exists rather than an error naming the
// cycle. A module that imports nothing cannot participate in one.
//
// The second reason arrived with the console, and it is the sharper one. These
// names sat in routes.ts beside Fastify, the operator package and the database
// module, so a browser that wanted to know where to send a request had to import
// all of it or restate it. The console restated it, and paid for the copy with a
// check that compared the two by reading this file's source text — a check that
// works, and that exists only because the import was impossible. Naming a url
// should cost nothing, and now it costs nothing.
//
// Kept as one object rather than a handful of constants because the console
// iterates it to prove it has a route for each, which stops a report from being
// added here and reachable from nowhere.

/**
 * Which activations may I read?
 *
 * ITS OWN CONSTANT AND NOT A KEY IN REPORT_ENDPOINTS, and the reason is
 * not cosmetic. Every name in that object is a report *of* an activation: the
 * url carries `:activationId`, xp/api/check.mjs builds a request by substituting
 * one into it, and the console iterates the object to prove it has a screen per
 * report. This url takes no activation, because it is the request a caller makes
 * when they have not got one. Adding it there would have made every one of those
 * three mechanisms wrong at once, and the console's is in a package this change
 * may not touch.
 *
 * It is audited alongside the four all the same, under the name `activations` in
 * `report_access` — see migration 013. A list of a client's activations is a
 * disclosure of their calendar, and read as nova_admin it is a disclosure of
 * Nova's whole book of business.
 *
 * No `/v1/reports` prefix, on xp/CONSOLE-REPORT.md section 4's own wording. The
 * four reports sit under that prefix because they are reports; this is the index
 * a console loads before it knows what to report on.
 */
export const ACTIVATIONS_ENDPOINT = "/v1/activations";

export const REPORT_ENDPOINTS = {
  operator: "/v1/reports/activations/:activationId/operator",
  export: "/v1/reports/activations/:activationId/export",
  reconciliation: "/v1/reports/activations/:activationId/reconciliation",
  /**
   * One activation, whole, with no query string.
   *
   * The absence is the design. A per-purpose count over a slice narrow enough
   * identifies a person, and this endpoint offers nothing to slice with: no
   * window, no device, no format. Anything added here is a disclosure channel
   * before it is a feature, and optIn.ts holds the argument.
   */
  optIn: "/v1/reports/activations/:activationId/opt-in",
  /**
   * One activation, whole, with no query string.
   *
   * Counts survey_question answers only. Job titles and the rest of the library
   * live in the same fields map and are not votes. Free text is a count of
   * answers, never the words. The absence of a window is the same design as
   * opt-in: a per-option count over a slice narrow enough identifies a person.
   */
  survey: "/v1/reports/activations/:activationId/survey",
} as const;

/**
 * Who registered at an activation, and one of them in full.
 *
 * THEIR OWN OBJECT AND NOT TWO KEYS IN REPORT_ENDPOINTS, on the same kind of
 * reasoning ACTIVATIONS_ENDPOINT is its own constant, and it is worth setting
 * out because these two urls do carry `:activationId` and so the directory's
 * particular reason does not apply.
 *
 * Two mechanisms read that object by shape rather than by name. xp/api/check.mjs
 * builds a request from each entry by substituting one activation id, which
 * cannot produce the record url: it has a second parameter, and a url still
 * carrying `:attendeeRef` would be requested literally and answer 404 in a way
 * that reads like a routing bug. And the console iterates the same object to
 * prove it has a screen per report, with an exhaustive switch that stops
 * compiling until there is one. That property is the reason the object exists
 * and it is a good one; it also means adding a name there is adding a screen
 * somewhere else, and the people screens are somebody else's change.
 *
 * So this is a sibling object. The reports are five and stay five, and these
 * two are audited alongside them under names of their own.
 */
export const ATTENDEE_ENDPOINTS = {
  list: "/v1/reports/activations/:activationId/attendees",
  /**
   * One person, and the reference in the path is the only way to ask for them.
   *
   * No search and no lookup by address, which is an absence rather than an
   * unfinished feature. An endpoint that turned an email into a person would be
   * the one route in this service that answers "is this address in your
   * records", and it would answer it for whoever held an admin credential and
   * a list of addresses. A reference comes from the list, and reading the list
   * is a recorded disclosure with a size on it.
   */
  record: "/v1/reports/activations/:activationId/attendees/:attendeeRef",
} as const;

/**
 * What files of this activation's people have already been produced.
 *
 * ITS OWN CONSTANT AND NOT A KEY IN REPORT_ENDPOINTS, on ATTENDEE_ENDPOINTS'
 * reasoning rather than ACTIVATIONS_ENDPOINT's: this url does carry
 * `:activationId`, so the directory's particular objection does not apply.
 *
 * What applies is the pair of mechanisms that read that object by shape.
 * xp/api/check.mjs builds a request from each entry by substituting one
 * activation id, which would work here; xp/console/check.mjs asserts that the
 * object has exactly five keys and that the console has a screen per key, with
 * an exhaustive switch that stops compiling until there is one. This read is
 * not a sixth report with a screen of its own. It is a section of the Data
 * screen, beside the export it is the history of, and adding a name there
 * would demand a screen that should not exist.
 *
 * IT IS ALSO NOT AUDITED, WHICH IS THE OTHER WAY IT IS UNLIKE EVERY NAME IN
 * THAT OBJECT. Every url there files a `report_access` row. This one reads that
 * table and writes nothing to it, and the argument is beside AUDITED_REPORTS in
 * audit.ts, which is where somebody adding a read will look.
 *
 * Under the `/v1/reports` prefix all the same. It is a report about one
 * activation, read by the same credentials on the same surface, and the prefix
 * is what says so.
 */
export const EXPORT_HISTORY_ENDPOINT = "/v1/reports/activations/:activationId/export-history";

/**
 * The addresses typed at this activation that were never proved.
 *
 * ITS OWN CONSTANT AND NOT A KEY IN REPORT_ENDPOINTS, on the reasoning
 * ATTENDEE_ENDPOINTS and EXPORT_HISTORY_ENDPOINT both set out: xp/console's
 * check asserts that object has exactly five keys and that the console holds a
 * screen per key, with an exhaustive switch that stops compiling until there is
 * one. There is no screen for this yet, that package is somebody else's change,
 * and a name added there would demand one.
 *
 * IT IS AUDITED ALL THE SAME, unlike the export history beside it, under the
 * name `unverified_attempts` in `report_access` and by migration 130. The two
 * reads sit next to each other in this file and are opposite on that point,
 * which is worth a sentence: the export history reads the audit table and so
 * cannot sensibly file a row in it, while this one hands over mailboxes and is
 * the read most in need of a record that it happened.
 *
 * `unverified-attempts` in the url and `unverified_attempts` in the audit,
 * which is the spelling convention this surface already follows for `opt-in`
 * and `opt_in`. Worth naming because the two strings are one grep apart and
 * somebody will one day wonder whether the difference means anything.
 *
 * Under the `/v1/reports` prefix, because it is a report about one activation
 * read by the same credential on the same surface. Not under `/v1/attend`,
 * which is the attendee's own unauthenticated surface: nothing a stranger at a
 * stand can reach should share a prefix with this.
 */
export const UNVERIFIED_ATTEMPTS_ENDPOINT =
  "/v1/reports/activations/:activationId/unverified-attempts";

/**
 * What happened at this activation, newest first.
 *
 * ITS OWN CONSTANT AND NOT A KEY IN REPORT_ENDPOINTS, on the reasoning the three
 * constants above set out and with the same edge as the last one: xp/console's
 * check asserts that object has exactly five keys and that the console holds a
 * screen per key, with an exhaustive switch that stops compiling until there is
 * one. There is a screen for this being built right now, in a package this
 * change may not touch, so a name added there would break that package's build
 * from outside it while its own author was working in it.
 *
 * WHICH IS A PROVISIONAL REASON AND WORTH SAYING SO. Unlike the export history,
 * which is a section of the Data screen and never wanted a key, this one is a
 * screen of its own and belongs in that object once the console has it. Moving
 * it is a one-line change here, a name in the console's switch, and nothing
 * else: the audit name, the constraint and the rate limit rule are all keyed on
 * this constant's value rather than on where it is declared.
 *
 * IT IS AUDITED, under the name `activity` in `report_access` and by migration
 * 131. The read is paged and a page of it is every consent every registered
 * attendee gave and withdrew and when, keyed on a reference the People screen
 * resolves to a person, so it is a disclosure with a size on it and the audit
 * row is what bounds it. 131's header answers the export history's three
 * grounds for filing nothing one at a time, because the two reads sit beside
 * each other and the difference is not obvious.
 *
 * Under the `/v1/reports` prefix, because it is a report about one activation
 * read by the same credentials on the same surface.
 */
export const ACTIVITY_ENDPOINT = "/v1/reports/activations/:activationId/activity";

/**
 * What the prize wheel gave away at this activation (156, decision C10): the
 * figures, and every spin a page at a time with when, which lane, who by
 * address, what it landed on, the claim code and whether it was collected.
 *
 * ITS OWN CONSTANT AND NOT A KEY IN REPORT_ENDPOINTS, on ACTIVITY_ENDPOINT's
 * reasoning: the console's check holds that object to five keys. Audited as
 * `wheel_results`, and read by whoever reads People, because it names people
 * as People does. The file of it is the export, under the wheel_results
 * purpose, which is the export's rules and not this route's.
 */
export const WHEEL_RESULTS_ENDPOINT = "/v1/reports/activations/:activationId/wheel-results";
